import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEventTiming } from './EventTimingContext';
import { SortableTimingRow } from './SortableTimingRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { WARMUP_ADD_OPTIONS, TIME_PER_SKATER_OPTIONS } from './constants';

export default function FreedanceStyledanceTab({ eventType }) {
  const {
    isSuperUser,
    schedules,
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    selectedScheduleId,
    setSelectedScheduleId,
    minutesPostWarmup,
    setMinutesPostWarmup,
    addForPanelSwap,
    setAddForPanelSwap,
    useSoloForCouples,
    setUseSoloForCouples,
    maxSkatersWarmupCadetSenior,
    setMaxSkatersWarmupCadetSenior,
    maxSkatersWarmupOther,
    setMaxSkatersWarmupOther,
    timingsLists,
    fetchTimings,
    sensors,
    handleDragEnd,
    canEditTiming,
    resetForm,
    calculateWarmupTotal,
    formatTimeOnBlur,
    shouldShowHR,
  } = useEventTiming();

  const [grades, setGrades] = useState([]);
  const [gradeTimings, setGradeTimings] = useState({});

  const timingsList = timingsLists[eventType] || [];
  const eventLabel = eventType === 'freedance' ? 'Freedance' : 'Styledance';
  const tabLabel = eventType === 'freedance' ? 'Solo FD' : 'Solo SD';

  // Fetch grades when schedule is selected
  useEffect(() => {
    if (!selectedScheduleId || editingTiming) {
      if (!editingTiming) setGrades([]);
      return;
    }

    const fetchGrades = async () => {
      try {
        const endpoint = eventType === 'freedance' 
          ? `${API}/event-timings/freedance-grades/${selectedScheduleId}`
          : `${API}/event-timings/styledance-grades/${selectedScheduleId}`;
        
        const res = await axios.get(endpoint, { withCredentials: true });
        const gradeData = res.data || [];
        setGrades(gradeData);
        
        const initialTimings = {};
        gradeData.forEach(g => {
          initialTimings[g.grade_id] = {
            programme_time: g.programme_time || '',
            add_for_warmup: '2:00',
            time_per_skater: '6:00',
            can_shorten: false
          };
        });
        setGradeTimings(initialTimings);
      } catch (error) {
        console.error('Error fetching grades:', error);
        toast.error('Failed to load grades for this schedule');
      }
    };
    fetchGrades();
  }, [selectedScheduleId, editingTiming, eventType]);

  const localResetForm = () => {
    resetForm();
    setGrades([]);
    setGradeTimings({});
  };

  const handleAddTimings = () => {
    localResetForm();
    setDialogOpen(true);
  };

  const handleEditTiming = async (timing) => {
    setEditingTiming(timing);
    setSelectedScheduleId(timing.schedule_id);
    setMinutesPostWarmup(timing.minutes_post_warmup_first_skater || '1:00');
    setAddForPanelSwap(timing.add_for_panel_swap || '1:00');
    setUseSoloForCouples(timing.use_solo_for_couples ?? true);
    setMaxSkatersWarmupCadetSenior(timing.max_skaters_warmup_cadet_senior ?? 5);
    setMaxSkatersWarmupOther(timing.max_skaters_warmup_other ?? 6);
    
    try {
      const endpoint = eventType === 'freedance'
        ? `${API}/event-timings/freedance-grades/${timing.schedule_id}`
        : `${API}/event-timings/styledance-grades/${timing.schedule_id}`;
      
      const res = await axios.get(endpoint, { withCredentials: true });
      const gradeData = res.data || [];
      setGrades(gradeData);
      
      const savedTimingsMap = {};
      (timing.grade_timings || []).forEach(gt => {
        savedTimingsMap[gt.grade_id] = gt;
      });
      
      const populatedTimings = {};
      gradeData.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        populatedTimings[g.grade_id] = {
          programme_time: g.programme_time || '',
          add_for_warmup: saved?.add_for_warmup || '2:00',
          time_per_skater: saved?.time_per_skater || '6:00',
          can_shorten: saved?.can_shorten || false
        };
      });
      setGradeTimings(populatedTimings);
    } catch (error) {
      console.error('Error fetching grades for edit:', error);
      toast.error('Failed to load timing data');
      return;
    }
    
    setDialogOpen(true);
  };

  const updateGradeTiming = (gradeId, field, value) => {
    setGradeTimings(prev => ({
      ...prev,
      [gradeId]: { ...prev[gradeId], [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!selectedScheduleId) {
      toast.error('Please select a schedule');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        schedule_id: selectedScheduleId,
        event_type: eventType,
        grade_timings: Object.entries(gradeTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          ...timing
        })),
        minutes_post_warmup_first_skater: minutesPostWarmup,
        add_for_panel_swap: addForPanelSwap,
        use_solo_for_couples: useSoloForCouples,
        max_skaters_warmup_cadet_senior: maxSkatersWarmupCadetSenior,
        max_skaters_warmup_other: maxSkatersWarmupOther
      };

      await axios.post(`${API}/event-timings`, payload, { withCredentials: true });
      
      toast.success(editingTiming ? `${eventLabel} timings updated successfully` : `${eventLabel} timings saved successfully`);
      setDialogOpen(false);
      localResetForm();
      fetchTimings(eventType);
    } catch (error) {
      console.error('Error saving timings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  const renderGradesTable = () => (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-green-100">
          <th className="border px-3 py-2 text-left w-40">Grade</th>
          <th className="border px-3 py-2 text-center w-36">Programme Time</th>
          <th className="border px-3 py-2 text-center w-28">Add for Warmup</th>
          <th className="border px-3 py-2 text-center w-24">Warm Up</th>
          <th className="border px-3 py-2 text-center w-28">Time per Skater</th>
          <th className="border px-3 py-2 text-center w-24">Can Shorten</th>
        </tr>
      </thead>
      <tbody>
        {grades.map((grade, index) => {
          const timing = gradeTimings[grade.grade_id] || {};
          const warmupTotal = calculateWarmupTotal(timing.programme_time, timing.add_for_warmup);
          const showHR = shouldShowHR(grades, index);
          
          return (
            <React.Fragment key={grade.grade_id}>
              {showHR && (
                <tr>
                  <td colSpan={6} style={{ border: 'none', padding: '16px 0', background: 'white' }}>
                    <div style={{ height: '2px', backgroundColor: '#a8a29e' }}></div>
                  </td>
                </tr>
              )}
              <tr>
                <td className="border px-3 py-2 bg-white font-medium">
                  {grade.name}
                  {(grade.section === 'solo_freedance' || grade.section === 'solo_styledance') && (
                    <span className="text-xs text-muted-foreground ml-1">({tabLabel})</span>
                  )}
                </td>
                <td className="border px-3 py-2 bg-white text-center text-sm" data-testid={`programme-time-${grade.grade_id}`}>
                  {timing.programme_time || '-'}
                </td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.add_for_warmup || '2:00'} 
                    onValueChange={(v) => updateGradeTiming(grade.grade_id, 'add_for_warmup', v)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {WARMUP_ADD_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-3 py-2 bg-gray-50 text-center font-medium">
                  {warmupTotal || '-'}
                </td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.time_per_skater || '6:00'} 
                    onValueChange={(v) => updateGradeTiming(grade.grade_id, 'time_per_skater', v)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {TIME_PER_SKATER_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-3 py-2 bg-white text-center">
                  <Checkbox
                    checked={timing.can_shorten || false}
                    onCheckedChange={(checked) => updateGradeTiming(grade.grade_id, 'can_shorten', checked)}
                    data-testid={`can-shorten-${grade.grade_id}`}
                  />
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{eventLabel}</h2>
        <Button onClick={handleAddTimings} className="btn-primary" data-testid={`add-timings-${eventType}`}>
          <Plus className="mr-2 h-4 w-4" />
          Add Timings
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create and edit timing tables for schedules you own. Timing tables for system schedules are view-only.
        </div>
      )}
      
      <Card className="p-6">
        {timingsList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No timing entries yet for {eventLabel}.</p>
            <p className="text-sm mt-1">Click "Add Timings" to create your first entry.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, eventType)}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8"></th>
                    <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                    <th className="text-center py-3 px-2 font-semibold">Grades Configured</th>
                    <th className="text-center py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={timingsList.map(t => t.timing_id)} strategy={verticalListSortingStrategy}>
                    {timingsList.map(timing => (
                      <SortableTimingRow
                        key={timing.timing_id}
                        id={timing.timing_id}
                        timing={timing}
                        canEdit={canEditTiming(timing)}
                        canReorder={isSuperUser}
                        onEdit={handleEditTiming}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) localResetForm(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTiming ? `Edit ${eventLabel} Timings` : `Add ${eventLabel} Timings`}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Select Schedule *</Label>
              <Select 
                value={selectedScheduleId} 
                onValueChange={setSelectedScheduleId}
                disabled={!!editingTiming}
              >
                <SelectTrigger data-testid="schedule-select">
                  <SelectValue placeholder="Choose a schedule..." />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {schedules.map(s => (
                    <SelectItem key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingTiming && (
                <p className="text-xs text-muted-foreground mt-1">Schedule cannot be changed when editing</p>
              )}
            </div>

            {grades.length > 0 && (
              <div className="overflow-x-auto">
                {renderGradesTable()}
              </div>
            )}

            {grades.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Minutes post warm up - first skater</Label>
                    <Input
                      value={minutesPostWarmup}
                      onChange={(e) => setMinutesPostWarmup(e.target.value)}
                      onBlur={() => formatTimeOnBlur(minutesPostWarmup, setMinutesPostWarmup)}
                      placeholder="1:00"
                      className="mt-1"
                      data-testid="minutes-post-warmup"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:30)</p>
                  </div>
                  
                  <div>
                    <Label>Add for panel swap - men in group</Label>
                    <Input
                      value={addForPanelSwap}
                      onChange={(e) => setAddForPanelSwap(e.target.value)}
                      onBlur={() => formatTimeOnBlur(addForPanelSwap, setAddForPanelSwap)}
                      placeholder="1:00"
                      className="mt-1"
                      data-testid="add-panel-swap"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:00)</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Max skaters per warm up group</Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-sm text-muted-foreground">Cadet-Senior & Couples</Label>
                        <Select 
                          value={String(maxSkatersWarmupCadetSenior)} 
                          onValueChange={(v) => setMaxSkatersWarmupCadetSenior(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Other Skaters</Label>
                        <Select 
                          value={String(maxSkatersWarmupOther)} 
                          onValueChange={(v) => setMaxSkatersWarmupOther(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Use solo timings for same grade couples event?</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`useSoloForCouples-${eventType}`}
                          checked={useSoloForCouples === true}
                          onChange={() => setUseSoloForCouples(true)}
                          data-testid="solo-for-couples-yes"
                        />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`useSoloForCouples-${eventType}`}
                          checked={useSoloForCouples === false}
                          onChange={() => setUseSoloForCouples(false)}
                          data-testid="solo-for-couples-no"
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={!selectedScheduleId || grades.length === 0 || saving}
                className="btn-primary"
              >
                {saving ? 'Saving...' : (editingTiming ? 'Update Timings' : 'Save Timings')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.schedule_name ? `timing for "${itemToDelete.schedule_name}"` : 'this timing'}
        loading={deleting}
      />
    </>
  );
}
